import { useEffect, useState } from 'react';

import type { PublicConfigResponse } from '@nexus/shared';

import { apiGet } from './api';

/**
 * What a signed-in creator may know about the operator's settings: the
 * review promise, the referral terms, where payouts come from. Fetched once
 * per page load and shared, because three screens ask and none of them
 * should each cost a round trip.
 *
 * `null` until it arrives or when it cannot: a screen renders without the
 * sentence rather than waiting on it.
 */
let cached: Promise<PublicConfigResponse | null> | null = null;

export function fetchPublicConfig(): Promise<PublicConfigResponse | null> {
  if (!cached) {
    cached = apiGet<PublicConfigResponse>('/settings/public').catch(() => {
      cached = null;
      return null;
    });
  }
  return cached;
}

/** For a test: forget what was fetched. */
export function resetPublicConfig(): void {
  cached = null;
}

export function usePublicConfig(): PublicConfigResponse | null {
  const [config, setConfig] = useState<PublicConfigResponse | null>(null);
  useEffect(() => {
    let live = true;
    void fetchPublicConfig().then((body) => {
      if (live) setConfig(body);
    });
    return () => {
      live = false;
    };
  }, []);
  return config;
}

/** "within 24 hours" / "within 1 hour", for the review promise. */
export function reviewPromise(hours: number | null | undefined): string {
  if (!hours || hours <= 0) return 'soon';
  return `within ${hours} hour${hours === 1 ? '' : 's'}`;
}
